import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { ActionButton } from '../../../../components/ActionButton'
import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { RichTextField } from '../../../../components/RichTextField'
import { SearchableSelect } from '../../../../components/Select'
import { useHasRole } from '../../../../helpers'
import { IBannerData, IBannerPageCardProps, Tags, tagsLabels } from '../../../../interfaces/Website/Banners/IBanners'
import BannerPreview from './BannerPreview'
import { getQueryParamsManually, isPersistedBanner, normalizeRichText } from './helpers'
import SiteColorInput from './SiteColorInput'
import { DEFAULT_TEXT_COLORS, tagStyle } from './siteColors'

const Error = styled(Text)`
  color: red.500;
  font-size: 0.8rem;
`

type ITagColorField = keyof Pick<
  IBannerData,
  | 'tag_online_color'
  | 'tag_online_ao_vivo_color'
  | 'tag_presencial_color'
  | 'tag_online_text_color'
  | 'tag_online_ao_vivo_text_color'
  | 'tag_presencial_text_color'
>

const TAG_COLOR_FIELDS: { tag: Tags; label: string; background: ITagColorField; text: ITagColorField }[] = [
  { tag: 'online', label: 'online', background: 'tag_online_color', text: 'tag_online_text_color' },
  {
    tag: 'online ao vivo',
    label: 'online ao vivo',
    background: 'tag_online_ao_vivo_color',
    text: 'tag_online_ao_vivo_text_color'
  },
  { tag: 'presencial', label: 'presencial', background: 'tag_presencial_color', text: 'tag_presencial_text_color' }
]

function BannerPageCard({
  data,
  fonts,
  onChange,
  errors,
  changePosition,
  openDeleteDialog,
  handleSwitch,
  isSwitchSaving
}: IBannerPageCardProps) {
  const [banner, setBanner] = useState(data)
  const preview = useDisclosure()
  const hasRole = useHasRole()
  const permissions = {
    editThisBanner: isPersistedBanner(data.id) ? hasRole('edit_banner') : hasRole('add_banner'),
    deleteBanner: hasRole('delete_banner')
  }

  useEffect(() => {
    if (!banner) {
      setMediaType(data.video_url ? 'video' : 'image')
      setMediaTypeMobile(data.video_url_mobile ? 'video' : 'image')
    }
    setBanner(data)
  }, [data])

  useEffect(() => {
    onChange(banner)
  }, [banner])

  const updateField = (field: string, value: string | string[] | File | null | boolean | number) => {
    setBanner({ ...banner, [field]: value })
  }

  const [mediaType, setMediaType] = useState<string>(data.video_url ? 'video' : 'image')
  const [mediaTypeMobile, setMediaTypeMobile] = useState<string>(data.video_url_mobile ? 'video' : 'image')

  function isYouTubeUrl(url: string): boolean {
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be|youtube-nocookie\.com).*?$/
    return youtubeRegex.test(url)
  }

  function getYouTubeVideoId(url: string): string | null {
    const params = getQueryParamsManually(url)

    return params.v || null
  }

  function isVimeoUrl(url: string): boolean {
    const vimeoRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:\w+\/)?videos\/|video\/)?(\d+)(?:[?&].*)?$/
    return vimeoRegex.test(url)
  }

  function getVimeoVideoId(url: string): string | null {
    const vimeoRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:\w+\/)?videos\/|video\/)?(\d+)(?:[?&].*)?$/
    const match = url.match(vimeoRegex)
    // O ID do vídeo é capturado no primeiro grupo (match[1])
    return match && match[1] ? match[1] : null
  }

  return (
    <Flex
      bg={!banner.is_active ? 'gray.100' : 'white'}
      borderRadius="md"
      p={5}
      borderWidth="2px"
      borderColor="gray.200"
      gap={4}
      flexWrap="wrap"
      shadow="md"
      _hover={{
        shadow: 'lg',
        transitionDuration: '0.3s'
      }}
    >
      <Stack flex={2}>
        {/* O card inteiro é a alça do arrasto (`BannerPageSortableBanner`): sem parar o
            `pointerdown` aqui, selecionar um trecho com o mouse para deixá-lo em negrito
            arrastaria o banner. */}
        <Stack spacing={3} onPointerDown={(e) => e.stopPropagation()}>
          <RichTextField
            label="Título"
            toolbar="banner"
            fonts={fonts}
            minH="60px"
            value={banner.title ?? ''}
            onChange={(html) => updateField('title', normalizeRichText(html))}
            isReadOnly={!permissions.editThisBanner}
            error={errors?.title}
          />
          <RichTextField
            label="Subtítulo"
            toolbar="banner"
            fonts={fonts}
            minH="60px"
            value={banner.subtitle ?? ''}
            onChange={(html) => updateField('subtitle', normalizeRichText(html))}
            isReadOnly={!permissions.editThisBanner}
            error={errors?.subtitle}
          />
          <RichTextField
            label="Texto do CTA"
            toolbar="banner"
            fonts={fonts}
            minH="40px"
            value={banner.cta_text ?? ''}
            onChange={(html) => updateField('cta_text', normalizeRichText(html))}
            isReadOnly={!permissions.editThisBanner}
            error={errors?.cta_text}
            helperText="No site o texto do botão sai em maiúsculas."
          />
        </Stack>
        <Box>
          <FormField label="Link do CTA" htmlFor="ctaLink">
            <Input
              id="ctaLink"
              size="md"
              bg="whiteAlpha"
              color="gray.900"
              borderWidth="1px"
              borderColor="gray.300"
              borderRadius="md"
              value={banner.cta_link}
              onChange={(e) => updateField('cta_link', e.target.value)}
              disabled={!permissions.editThisBanner}
            />
          </FormField>
          {errors?.cta_link && <Error color="red.500">{errors.cta_link[0]}</Error>}
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={3} py={2}>
          <HStack spacing={4}>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="titleColor"
                  stored={banner.title_color}
                  fallback={DEFAULT_TEXT_COLORS.title}
                  onChange={(value) => updateField('title_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="titleColor" fontSize="sm" m={0}>
                  Cor do Título
                </FormLabel>
              </FormControl>
              {errors?.title_color && <Error color="red.500">{errors.title_color[0]}</Error>}
            </Box>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="subtitleColor"
                  stored={banner.subtitle_color}
                  fallback={DEFAULT_TEXT_COLORS.subtitle}
                  onChange={(value) => updateField('subtitle_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="subtitleColor" fontSize="sm" m={0}>
                  Cor do Subtítulo
                </FormLabel>
              </FormControl>
              {errors?.subtitle_color && <Error color="red.500">{errors.subtitle_color[0]}</Error>}
            </Box>
          </HStack>
          <HStack spacing={4}>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="buttonTextColor"
                  stored={banner.button_text_color}
                  fallback={DEFAULT_TEXT_COLORS.buttonText}
                  onChange={(value) => updateField('button_text_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="buttonTextColor" fontSize="sm" m={0}>
                  Cor do Texto do Botão
                </FormLabel>
              </FormControl>
              {errors?.button_text_color && <Error color="red.500">{errors.button_text_color[0]}</Error>}
            </Box>
            <Box w="50%">
              <FormControl w="auto" display="flex" alignItems="center" gap={2}>
                <SiteColorInput
                  id="buttonColor"
                  stored={banner.button_color}
                  fallback={null}
                  onChange={(value) => updateField('button_color', value)}
                  disabled={!permissions.editThisBanner}
                />
                <FormLabel htmlFor="buttonColor" fontSize="sm" m={0}>
                  Cor do Botão
                </FormLabel>
              </FormControl>
              {errors?.button_color && <Error color="red.500">{errors.button_color[0]}</Error>}
            </Box>
          </HStack>
        </Box>
        <Box>
          <FormField label="Gradiente">
            <Box w="33%">
              <Flex align="center" gap={2}>
                <Switch
                  title={data.is_gradient_active ? 'Desativar Gradiente do Banner' : 'Ativar Gradiente do Banner'}
                  isChecked={!!data.is_gradient_active}
                  onChange={(e) => updateField('is_gradient_active', e.target.checked ? 1 : 0)}
                  isDisabled={!permissions.editThisBanner}
                />
                <SiteColorInput
                  id="gradientColor"
                  disabled={!data.is_gradient_active || !permissions.editThisBanner}
                  stored={banner.gradient_color}
                  fallback={null}
                  onChange={(value) => updateField('gradient_color', value)}
                />
                <FormLabel htmlFor="gradientColor" fontSize="sm" m={0}>
                  Cor do Gradiente
                </FormLabel>
              </Flex>
              {errors?.gradient_color && <Error color="red.500">{errors.gradient_color[0]}</Error>}
            </Box>
          </FormField>
        </Box>
        <Box>
          <FormField label="Tags">
            <SearchableSelect
              isMulti
              value={banner.tags ?? []}
              options={[
                { label: tagsLabels.online, value: 'online' },
                { label: tagsLabels['online ao vivo'], value: 'online ao vivo' },
                { label: tagsLabels.presencial, value: 'presencial' }
              ]}
              onChange={(tags) => updateField('tags', tags)}
              placeholder="Selecione uma ou mais tags"
              entity="tag"
              feminine
              isDisabled={!permissions.editThisBanner}
            />
          </FormField>
          {errors?.tags && <Error color="red.500">{errors.tags[0]}</Error>}
          <Stack pt={5} spacing={3}>
            {TAG_COLOR_FIELDS.map(({ tag, label, background, text }) => {
              const isDisabled = !banner.tags?.includes(tag) || !permissions.editThisBanner
              const colors = tagStyle(banner, tag)

              return (
                <HStack key={tag} spacing={4} align="start">
                  <FormField w="50%" error={errors?.[background]}>
                    <Flex align="center" gap={2}>
                      <SiteColorInput
                        disabled={isDisabled}
                        stored={banner[background]}
                        fallback={colors.background}
                        onChange={(value) => updateField(background, value)}
                      />
                      <FormLabel fontSize="sm" m={0}>
                        Cor da tag {label}
                      </FormLabel>
                    </Flex>
                  </FormField>
                  <FormField w="50%" error={errors?.[text]}>
                    <Flex align="center" gap={2}>
                      <SiteColorInput
                        disabled={isDisabled}
                        stored={banner[text]}
                        fallback={colors.color}
                        onChange={(value) => updateField(text, value)}
                      />
                      <FormLabel fontSize="sm" m={0}>
                        Cor do texto da tag {label}
                      </FormLabel>
                    </Flex>
                  </FormField>
                </HStack>
              )
            })}
          </Stack>
        </Box>
      </Stack>

      {/* image/video select */}
      <Stack flex={1} spacing={4}>
        <Box onPointerDown={(event) => event.stopPropagation()}>
          <ActionButton action="view" label="Pré-visualizar banner" showLabel onClick={preview.onOpen} />
        </Box>
        <Tabs isLazy>
          <TabList>
            <Tab>Desktop</Tab>
            <Tab>Mobile</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Stack flex={1} spacing={4}>
                <RadioGroup
                  onChange={setMediaType}
                  value={mediaType}
                  colorScheme="gray"
                  isDisabled={!permissions.editThisBanner}
                >
                  <Stack direction="row" color="gray.700">
                    <Radio value="image">Imagem</Radio>
                    <Radio value="video">Vídeo</Radio>
                  </Stack>
                </RadioGroup>
                {mediaType === 'image' ? (
                  <>
                    <ImageCropInput
                      value={banner.image ?? null}
                      onChange={(file) => updateField('image', file)}
                      aspectRatio={1200 / 420}
                      suggestedSize="420px x 1200px"
                      previewAspectRatio="1200 / 420"
                      isDisabled={!permissions.editThisBanner}
                      error={errors?.image}
                    />
                  </>
                ) : (
                  <>
                    <Input
                      placeholder="Insira a URL do vídeo"
                      value={banner.video_url}
                      onChange={(e) => updateField('video_url', e.target.value)}
                      type="url"
                      color="gray.900"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      px={4}
                      py={2}
                      _hover={{
                        borderColor: 'gray.500',
                        '&::placeholder': {
                          color: 'gray.700'
                        }
                      }}
                      _focus={{
                        borderColor: 'gray.600',
                        boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
                      }}
                      _placeholder={{ color: 'gray.500' }}
                      disabled={!permissions.editThisBanner}
                    />
                    {errors?.video_url && <Error color="red.500">{errors.video_url[0]}</Error>}

                    {banner.video_url && (
                      <>
                        {isVimeoUrl(banner.video_url) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              src={`https://player.vimeo.com/video/${getVimeoVideoId(banner.video_url)}`}
                              title="Vídeo Preview"
                              width="100%"
                              height="200"
                              allow="autoplay; fullscreen"
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px'
                              }}
                            />
                          </Box>
                        )}
                        {isYouTubeUrl(banner.video_url) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              width="100%"
                              height="200"
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(banner.video_url)}`}
                              title="YouTube video player"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </TabPanel>
            <TabPanel>
              <Stack flex={1} spacing={4}>
                <RadioGroup
                  onChange={setMediaTypeMobile}
                  value={mediaTypeMobile}
                  colorScheme="gray"
                  isDisabled={!permissions.editThisBanner}
                >
                  <Stack direction="row" color="gray.700">
                    <Radio value="image">Imagem</Radio>
                    <Radio value="video">Vídeo</Radio>
                  </Stack>
                </RadioGroup>
                {mediaTypeMobile === 'image' ? (
                  <>
                    <ImageCropInput
                      value={banner.image_mobile ?? null}
                      onChange={(file) => updateField('image_mobile', file)}
                      aspectRatio={390 / 413}
                      suggestedSize="390px x 413px"
                      previewAspectRatio="390 / 413"
                      isDisabled={!permissions.editThisBanner}
                      error={errors?.image_mobile}
                    />
                  </>
                ) : (
                  <>
                    <Input
                      placeholder="Insira a URL do vídeo"
                      value={banner.video_url_mobile}
                      onChange={(e) => updateField('video_url_mobile', e.target.value)}
                      type="url"
                      color="gray.900"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      px={4}
                      py={2}
                      _hover={{
                        borderColor: 'gray.500',
                        '&::placeholder': {
                          color: 'gray.700'
                        }
                      }}
                      _focus={{
                        borderColor: 'gray.600',
                        boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
                      }}
                      _placeholder={{ color: 'gray.500' }}
                      disabled={!permissions.editThisBanner}
                    />
                    {errors?.video_url_mobile && <Error color="red.500">{errors.video_url_mobile[0]}</Error>}

                    {banner.video_url_mobile && (
                      <>
                        {isVimeoUrl(banner.video_url_mobile) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              src={`https://player.vimeo.com/video/${getVimeoVideoId(banner.video_url_mobile)}`}
                              title="Vídeo Preview"
                              width="100%"
                              height="200"
                              allow="autoplay; fullscreen"
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px'
                              }}
                            />
                          </Box>
                        )}
                        {isYouTubeUrl(banner.video_url_mobile) && (
                          <Box mt={3} borderRadius="md" overflow="hidden">
                            <iframe
                              width="100%"
                              height="200"
                              src={`https://www.youtube.com/embed/${getYouTubeVideoId(banner.video_url_mobile)}`}
                              title="YouTube video player"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            ></iframe>
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <HStack spacing={4} justify="end">
          {permissions.editThisBanner && (
            <HStack>
              <IconButton
                icon={<ChevronUpIcon />}
                aria-label="Mover para cima"
                title="Mover para cima"
                colorScheme="gray"
                size="sm"
                onClick={() => changePosition('up')}
              />
              <IconButton
                icon={<ChevronDownIcon />}
                aria-label="Mover para baixo"
                title="Mover para baixo"
                colorScheme="gray"
                size="sm"
                onClick={() => changePosition('down')}
              />
            </HStack>
          )}
          <Switch
            title={banner.is_active ? 'Desativar Banner' : 'Ativar Banner'}
            isChecked={!!data.is_active}
            hidden={!isPersistedBanner(data.id)}
            onChange={(e) => handleSwitch(e, data.id)}
            isDisabled={!permissions.editThisBanner || isSwitchSaving(data.id)}
          />
          {permissions.deleteBanner && (
            <Button colorScheme="red" size="sm" onClick={openDeleteDialog}>
              Excluir
            </Button>
          )}
        </HStack>
      </Stack>
      <BannerPreview
        banner={banner}
        desktopMediaType={mediaType}
        mobileMediaType={mediaTypeMobile}
        isOpen={preview.isOpen}
        onClose={preview.onClose}
      />
    </Flex>
  )
}

export default BannerPageCard
